import type { PayloadAction } from '@reduxjs/toolkit';
import { createReducer } from '@reduxjs/toolkit';
import {
  deleteCustomerContractFailure,
  deleteCustomerContractRequest,
  deleteCustomerContractsuccess,
  fetchCustomerContractsFailure,
  fetchCustomerContractsRequest,
  fetchCustomerContractsSuccess,
  fetchSingleCustomerContractFailure,
  fetchSingleCustomerContractRequest,
  fetchSingleCustomerContractsuccess,
  upsertCustomerContractFailure,
  upsertCustomerContractRequest,
  upsertCustomerContractsuccess,
} from 'actions/customerContracts';

import { API, CustomerContract, ErrorObject, UUID } from 'types';

type EntityState = {
  loading: string[];
  error: ErrorObject | null;
  contracts: CustomerContract[];
  activeContract: CustomerContract | null;
  customerContractsCount: number;
};

const defaultState: EntityState = {
  loading: [],
  error: null,
  contracts: [],
  activeContract: null,
  customerContractsCount: 0,
};
type CustomerContractReducer<P = void> = (
  state: EntityState,
  payload: PayloadAction<P>
) => EntityState;

const handleFetchSingleCustomerContractRequest: CustomerContractReducer = (state) => ({
  ...state,
  loading: [...state.loading, fetchSingleCustomerContractRequest.toString()],
});

const handleFetchSingleCustomerContractsuccess: CustomerContractReducer<CustomerContract> = (
  state,
  { payload }
) => ({
  ...state,
  contracts: [...state.contracts, payload],
  activeContract: payload,
  loading: state.loading.filter((s) => s !== fetchSingleCustomerContractRequest.toString()),
});

const handleFetchSingleCustomerContractFailure: CustomerContractReducer<ErrorObject> = (
  state,
  { payload }
) => ({
  ...state,
  error: payload,
  loading: state.loading.filter((s) => s !== fetchSingleCustomerContractRequest.toString()),
});

const handleUpsertCustomerContractRequest: CustomerContractReducer = (state) => ({
  ...state,
  loading: [...state.loading, upsertCustomerContractRequest.toString()],
});

const handleUpsertCustomerContractsuccess: CustomerContractReducer<CustomerContract> = (
  state,
  { payload }
) => {
  const contract = state.contracts.find((p) => p.uuid === payload.uuid);

  if (contract) {
    return {
      ...state,
      contracts: state.contracts.map((p) => (p.uuid === payload.uuid ? { ...p, ...payload } : p)),
      activeContract: { ...contract, ...payload },
      loading: state.loading.filter((s) => s !== upsertCustomerContractRequest.toString()),
    };
  }

  return {
    ...state,
    contracts: [payload, ...state.contracts],
    loading: state.loading.filter((s) => s !== upsertCustomerContractRequest.toString()),
    customerContractsCount: state.customerContractsCount + 1,
  };
};

const handleUpsertCustomerContractFailure: CustomerContractReducer<ErrorObject> = (
  state,
  { payload }
) => ({
  ...state,
  error: payload,
  loading: state.loading.filter((s) => s !== upsertCustomerContractRequest.toString()),
});

const handleDeleteCustomerContractRequest: CustomerContractReducer = (state) => ({
  ...state,
  loading: [...state.loading, deleteCustomerContractRequest.toString()],
});

const handleDeleteCustomerContractsuccess: CustomerContractReducer<{ uuid: UUID }> = (
  state,
  { payload }
) => {
  const newContracts = state.contracts.filter((p) => p.uuid !== payload.uuid);
  return {
    ...state,
    contracts: newContracts,
    loading: state.loading.filter((s) => s !== deleteCustomerContractRequest.toString()),
    customerContractsCount: state.customerContractsCount - 1,
  };
};

const handleDeleteCustomerContractFailure: CustomerContractReducer<ErrorObject> = (
  state,
  { payload }
) => ({
  ...state,
  error: payload,
  loading: state.loading.filter((s) => s !== deleteCustomerContractRequest.toString()),
});

const handleFetchCustomerContractsRequest: CustomerContractReducer = (state) => ({
  ...state,
  loading: [...state.loading, fetchCustomerContractsRequest.toString()],
});

const handleFetchCustomerContractsSuccess: CustomerContractReducer<
  API.WrappedAPIResponse<CustomerContract>
> = (state, { payload }) => ({
  ...state,
  contracts: payload.records,
  customerContractsCount: payload.count,
  loading: state.loading.filter((s) => s !== fetchCustomerContractsRequest.toString()),
});

const handleFetchCustomerContractsFailure: CustomerContractReducer<ErrorObject> = (
  state,
  { payload }
) => ({
  ...state,
  error: payload,
  loading: state.loading.filter((s) => s !== fetchCustomerContractsRequest.toString()),
});

const handlers = {
  [fetchCustomerContractsRequest.toString()]: handleFetchCustomerContractsRequest,
  [fetchCustomerContractsFailure.toString()]: handleFetchCustomerContractsFailure,
  [fetchCustomerContractsSuccess.toString()]: handleFetchCustomerContractsSuccess,

  [fetchSingleCustomerContractsuccess.toString()]: handleFetchSingleCustomerContractsuccess,
  [fetchSingleCustomerContractFailure.toString()]: handleFetchSingleCustomerContractFailure,
  [fetchSingleCustomerContractRequest.toString()]: handleFetchSingleCustomerContractRequest,

  [upsertCustomerContractRequest.toString()]: handleUpsertCustomerContractRequest,
  [upsertCustomerContractFailure.toString()]: handleUpsertCustomerContractFailure,
  [upsertCustomerContractsuccess.toString()]: handleUpsertCustomerContractsuccess,

  [deleteCustomerContractRequest.toString()]: handleDeleteCustomerContractRequest,
  [deleteCustomerContractsuccess.toString()]: handleDeleteCustomerContractsuccess,
  [deleteCustomerContractFailure.toString()]: handleDeleteCustomerContractFailure,
};

const entitiesReducer = createReducer(defaultState, handlers);

export default entitiesReducer;
